import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  StarIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  UsersIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const SubscriptionPlans = ({ plans = [] }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // Delete plan mutation
  const deletePlanMutation = useMutation(
    (planId) => axios.delete(`/subscriptions/plans/${planId}`),
    {
      onSuccess: () => {
        toast.success('Plan supprimé avec succès!');
        queryClient.invalidateQueries('subscription-plans');
      },
      onError: () => {
        toast.error('Erreur lors de la suppression du plan');
      },
    }
  );

  const handleDeletePlan = (planId, planName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le plan "${planName}" ?`)) {
      deletePlanMutation.mutate(planId);
    }
  };

  const getPlanTypeColor = (type) => {
    switch (type) {
      case 'daily':
        return 'bg-blue-500/20 text-blue-400';
      case 'weekly':
        return 'bg-green-500/20 text-green-400';
      case 'monthly':
        return 'bg-purple-500/20 text-purple-400';
      case 'quarterly':
        return 'bg-orange-500/20 text-orange-400';
      case 'semiannual':
        return 'bg-pink-500/20 text-pink-400';
      case 'annual':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getPlanTypeName = (type) => {
    const types = {
      daily: 'Quotidien',
      weekly: 'Hebdomadaire',
      monthly: 'Mensuel',
      quarterly: 'Trimestriel',
      semiannual: 'Semestriel',
      annual: 'Annuel',
    };
    return types[type] || type;
  };

  const defaultPlans = [
    {
      id: 'plan-daily',
      name: 'Pass Journalier',
      type: 'daily',
      duration: '1 jour',
      price: 500,
      description: 'Accès complet pour une journée',
      features: ['Accès à tous les équipements', 'Vestiaires', 'Douches'],
      isPopular: false,
      subscriberCount: 0,
    },
    {
      id: 'plan-monthly',
      name: 'Abonnement Mensuel',
      type: 'monthly',
      duration: '1 mois',
      price: 8500,
      description: 'Le plan le plus populaire',
      features: ['Accès illimité', 'Tous équipements', 'Vestiaires & Douches', 'Coaching gratuit'],
      isPopular: true,
      subscriberCount: 0,
    },
    {
      id: 'plan-annual',
      name: 'Abonnement Annuel',
      type: 'annual',
      duration: '12 mois',
      price: 85000,
      description: 'Meilleure économie sur l\'année',
      features: ['Accès illimité', 'Tous équipements', 'Vestiaires & Douches', 'Coaching personnel', 'Plan nutritionnel', '2 mois gratuits'],
      isPopular: false,
      subscriberCount: 0,
    },
  ];

  const plansToShow = plans.length > 0 ? plans : defaultPlans;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Plans d'Abonnement</h2>
          <p className="text-white/60 mt-1">Gérez vos plans et tarifs</p>
        </div>
        <Link
          to="/subscriptions/plans/new"
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouveau Plan</span>
        </Link>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plansToShow.map((plan, index) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border transition-all duration-300 hover:bg-white/15 ${
              plan.isPopular 
                ? 'border-purple-500/50 shadow-lg shadow-purple-500/20' 
                : 'border-white/20'
            }`}
          >
            {/* Popular Badge */}
            {plan.isPopular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                  <StarIcon className="h-4 w-4" />
                  <span>Populaire</span>
                </div>
              </div>
            )}

            {/* Plan Header */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlanTypeColor(plan.type)}`}>
                  {getPlanTypeName(plan.type)}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
              <p className="text-white/60 text-sm mb-4">{plan.description}</p>
              
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">{plan.price.toLocaleString()}</span>
                <span className="text-white/60 ml-1">DA</span>
                <p className="text-white/50 text-sm mt-1">{plan.duration}</p>
              </div>
            </div>

            {/* Features */}
            <div className="mb-6">
              <ul className="space-y-2">
                {plan.features && plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-white/80 text-sm">
                    <CheckIcon className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6 p-3 bg-white/5 rounded-xl">
              <div className="text-center">
                <UsersIcon className="h-5 w-5 text-white/50 mx-auto mb-1" />
                <p className="text-white font-semibold text-sm">{plan.subscriberCount || 0}</p>
                <p className="text-white/50 text-xs">Abonnés</p>
              </div>
              <div className="text-center">
                <CurrencyDollarIcon className="h-5 w-5 text-white/50 mx-auto mb-1" />
                <p className="text-white font-semibold text-sm">
                  {((plan.subscriberCount || 0) * plan.price).toLocaleString()}
                </p>
                <p className="text-white/50 text-xs">Revenus</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              <Link
                to={`/subscriptions/plans/${plan.id}/edit`}
                className="flex-1 bg-blue-500/20 text-blue-400 py-2 px-4 rounded-xl hover:bg-blue-500/30 transition-colors text-center font-medium"
              >
                <PencilIcon className="h-4 w-4 inline mr-2" />
                Modifier
              </Link>
              
              <button
                onClick={() => handleDeletePlan(plan.id, plan.name)}
                disabled={deletePlanMutation.isLoading}
                className="bg-red-500/20 text-red-400 py-2 px-4 rounded-xl hover:bg-red-500/30 transition-colors disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        ))}

        {/* Add New Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: plansToShow.length * 0.1 }}
        >
          <Link
            to="/subscriptions/plans/new"
            className="group block h-full bg-white/5 backdrop-blur-lg rounded-3xl p-6 border border-white/20 border-dashed hover:bg-white/10 hover:border-white/30 transition-all duration-300"
          >
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <PlusIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Créer un Nouveau Plan</h3>
              <p className="text-white/60 text-sm">
                Ajoutez un nouveau plan d'abonnement avec tarifs personnalisés
              </p>
            </div>
          </Link>
        </motion.div>
      </div>

      {/* Plans Statistics */}
      {plansToShow.length > 0 && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">Statistiques des Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-white">{plansToShow.length}</p>
              <p className="text-white/60 text-sm">Plans Actifs</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-white">
                {plansToShow.reduce((total, plan) => total + (plan.subscriberCount || 0), 0)}
              </p>
              <p className="text-white/60 text-sm">Total Abonnés</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-white">
                {plansToShow.reduce((total, plan) => total + ((plan.subscriberCount || 0) * plan.price), 0).toLocaleString()}
              </p>
              <p className="text-white/60 text-sm">Revenus Totaux (DA)</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-white">
                {plansToShow.length > 0 ? Math.round(plansToShow.reduce((total, plan) => total + plan.price, 0) / plansToShow.length).toLocaleString() : 0}
              </p>
              <p className="text-white/60 text-sm">Prix Moyen (DA)</p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SubscriptionPlans; 